import constants from "./constants";
import moment from "moment-timezone";
import toast from "react-hot-toast";
import FileSaver from "file-saver";
import { sortBy } from "lodash";
import defaultStaffRounded from "assets/images/staff-default-rounded.png";
import defaultStaffCircular from "assets/images/staff-default.svg";

import {
  BlobServiceClient,
  AnonymousCredential,
  newPipeline,
} from "@azure/storage-blob"; // Import Azure Storage Blob SDK
import { v4 as uuidv4 } from "uuid"; // Import UUID library

export function formatDate(date, format) {
  const localDate = toLocalDate(date);
  if (format) return moment(localDate).format(format);
  return moment(localDate).format("MMM DD, YYYY");
}
export function isValidDate(dateStr) {
  if (!dateStr) return false;
  const date = new Date(dateStr);
  return !isNaN(date.getTime());
}
export function toLocalDate(utcDate) {
  const localDate = new Date(utcDate);
  return localDate;
}
export function isMobileDevice() {
  if (navigator?.userAgent) {
    return /Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator?.userAgent
    );
  }
  return false;
}
export function setTimeToStartTime(date) {
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    0,
    0,
    0,
    0
  );
}

/** Default report range: 1st of current month through today (local timezone). */
export function getCurrentMonthToTodayDateRange() {
  return {
    from: moment().startOf("month").toDate(),
    to: moment().startOf("day").toDate(),
  };
}

/** Parses a stored date value without UTC day-shift (YYYY-MM-DD or ISO/Date). */
export function parseStoredDateValue(value) {
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [y, m, d] = value.split("-").map(Number);
    return new Date(y, m - 1, d);
  }
  return new Date(value);
}

/**
 * Fixes date ranges where "from" was stored one day before month start
 * (caused by `new Date("YYYY-MM-DD")` UTC parsing).
 */
export function normalizeStoredDateRange({ from, to }) {
  let fromM = moment(parseStoredDateValue(from)).startOf("day");
  let toM = moment(parseStoredDateValue(to)).startOf("day");
  const monthStart = toM.clone().startOf("month");

  if (fromM.clone().add(1, "day").isSame(monthStart, "day")) {
    fromM = monthStart;
  }

  return clampDateRangeToToday({ from: fromM.toDate(), to: toM.toDate() });
}

/**
 * Ensures the range end is not after today in the local timezone
 * (e.g. after switching from a timezone where "today" was ahead).
 */
export function clampDateRangeToToday({ from, to }) {
  const today = moment().startOf("day");
  let fromM = moment(from).startOf("day");
  let toM = moment(to).startOf("day");

  if (toM.isAfter(today)) {
    toM = today;
  }
  if (fromM.isAfter(toM)) {
    fromM = toM.clone().startOf("month");
  }

  return { from: fromM.toDate(), to: toM.toDate() };
}

/** True when the range end is not today's date in the local timezone. */
export function shouldShowGoToCurrentDateButton(to) {
  return !moment(to).startOf("day").isSame(moment().startOf("day"), "day");
}

/** True when the range end is today or later (Next navigation should be disabled). */
export function isDateRangeEndAtOrAfterToday(to) {
  return moment(to)
    .startOf("day")
    .isSameOrAfter(moment().startOf("day"), "day");
}

/** Serializes a date range for session storage (timezone-safe). */
export function serializeDateRangeForStorage({ from, to }) {
  return {
    from: moment(from).format("YYYY-MM-DD"),
    to: moment(to).format("YYYY-MM-DD"),
  };
}

export function constructMomentFromTime(hours, minutes, seconds = 0) {
  const m = moment();
  return m.hours(hours).minutes(minutes).seconds(seconds);
}

export function encodeId(id) {
  if (id === undefined || id === null) {
    return "";
  }
  return window.btoa(unescape(encodeURIComponent(String(id))));
}

export function decodeId(encodedId) {
  try {
    if (encodedId == null || encodedId === "") {
      return encodedId;
    }
    return decodeURIComponent(escape(window.atob(encodedId)));
  } catch (error) {
    console.warn("decodeId: invalid id token", error);
    return undefined;
  }
}

export function getDoctorFullName(firstName, lastName, titleId) {
  if (titleId === constants.titleIds.dr) {
    return `${
      constants.titles.find((it) => it.id === constants.titleIds.dr).text
    } ${firstName} ${lastName}`;
  }

  return `${firstName} ${lastName}`;
}

export const getFullAddress = (location, t) => {
  let fullAddress = "";
  const address = location?.address || "";
  const city = location?.city || "";
  const state = location?.state || "";
  const country = location?.country || "";
  if (address && (city || state || country)) {
    fullAddress =
      address +
      (city ? ", " + city : city) +
      (city || state ? ", " + state : state) +
      (city || state || country ? ", " + country : country);
  }
  return fullAddress ? fullAddress : t("notAdded");
};

/*** Generate Weekdays  ***/
export function generateWeek(startDate, endDate) {
  const dates = [];

  const currDate = moment(startDate).subtract(1, "days");

  while (currDate.add(1, "days").diff(endDate) < 0) {
    dates.push({
      mDate: currDate.clone().format("MMM DD, ddd"),
      date: currDate.clone(),
    });
  }

  return dates;
}

// generateCalanderMonthView
export function generateCalanderMonthView(currentMoment) {
  let startDate = moment(currentMoment).startOf("month").startOf("isoweek");
  let endDate = moment(currentMoment).endOf("month").endOf("isoweek");

  const currDate = moment(startDate).subtract(1, "days");

  let dates = [];
  while (currDate.add(1, "days").diff(endDate) < 0) {
    dates.push({
      mDate: currDate.clone().format("MMM DD, ddd"),
      date: currDate.clone(),
      openModel: false,
      isData: true,
    });
  }
  return dates;
}

export function generateCalanderMonthViewEvent(currentMoment) {
  let startDate = moment(currentMoment).startOf("month").startOf("isoweek");
  let endDate = moment(currentMoment).endOf("month");

  const currDate = moment(startDate).subtract(1, "days");

  let dates = [];
  while (currDate.add(1, "days").diff(endDate) < 0) {
    dates.push({
      mDate: currDate.clone().format("MMM DD, ddd"),
      date: currDate.clone(),
      openModel: false,
      isData: true,
    });
  }
  return dates;
}

export function generateMonth(currentMoment) {
  let startDate = moment(currentMoment).startOf("month");
  let endDate = moment(currentMoment).endOf("month");

  const currDate = moment(startDate).subtract(1, "days");

  let dates = [];
  while (currDate.add(1, "days").diff(endDate) < 0) {
    dates.push({
      mDate: currDate.clone().format("MMM DD, ddd"),
      date: currDate.clone(),
    });
  }
  return dates;
}

/*** Generate Month  ***/
export function fillDates(currentMoment) {
  const firstOfMonth = moment(currentMoment).startOf("month").day();
  const lastOfMonth = moment(currentMoment).endOf("month").day();

  const firstDayOfGrid = moment(currentMoment)
    .startOf("month")
    .subtract(firstOfMonth, "days");
  const lastDayOfGrid = moment(currentMoment)
    .endOf("month")
    .subtract(lastOfMonth, "days")
    .add(7, "days");

  const startCalendar = firstDayOfGrid.date();
  return this.rangeArray(
    startCalendar,
    startCalendar + lastDayOfGrid.diff(firstDayOfGrid, "days") - 1
  ).map((date) => {
    return moment(firstDayOfGrid).date(date);
  });
}

export function updateTimeToUpcomingQuater(date) {
  if (date && new Date(date)) {
    let datetime = new Date(date);

    let hours = datetime.getHours();
    let minutes = datetime.getMinutes();

    let minutesToSettle = minutes;
    let hoursToSettle = hours;

    if (minutes >= 1 && minutes <= 15) {
      minutesToSettle = 15;
    } else if (minutes >= 16 && minutes <= 30) {
      minutesToSettle = 30;
    } else if (minutes >= 31 && minutes <= 45) {
      minutesToSettle = 45;
    } else if (minutes >= 46 && minutes <= 60) {
      minutesToSettle = 0;
      hoursToSettle = hours + 1;
    }

    datetime.setHours(hoursToSettle, minutesToSettle, 0, 0);
    return moment(datetime, [moment.ISO_8601, "hh:mm A"]);
  } else {
    return date;
  }
}

export function getDay(date) {
  const weekday = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];
  let day = weekday[moment(date).day()];
  return {
    day: day,
    index: moment(date).day(),
  };
}
export function isMobileTab() {
  let width = window.innerWidth;
  let returnValue = width <= 768 ? true : false;
  return returnValue;
}

export const b64toBlob = (b64Data, contentType = "", sliceSize = 512) => {
  const byteCharacters = atob(b64Data);
  const byteArrays = [];

  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize);

    const byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }

  const blob = new Blob(byteArrays, { type: contentType });
  return blob;
};

/**
 * Strips path components and unsafe characters from a suggested download filename.
 * @param {string} [fileName]
 * @returns {string}
 */
export function sanitizeDownloadFileName(fileName) {
  if (!fileName || typeof fileName !== "string") {
    return "download";
  }
  const base = fileName.replace(/[^a-zA-Z0-9._-]/g, "_");
  return base.length > 0 ? base : "download";
}

const DOWNLOAD_MIME_TYPE_PATTERN =
  /^[a-z0-9][a-z0-9!#$&^_.+-]*\/[a-z0-9][a-z0-9!#$&^_.+-]*$/i;

/**
 * Normalizes Content-Type for Blob creation; rejects malformed or script-like values.
 * @param {string} [contentType]
 * @returns {string}
 */
export function sanitizeDownloadContentType(contentType) {
  const raw =
    contentType && typeof contentType === "string" ? contentType.trim() : "";
  if (!raw || raw.length > 256) {
    return "application/octet-stream";
  }
  if (/javascript|vbscript/i.test(raw)) {
    return "application/octet-stream";
  }
  const mainType = raw.split(";")[0].trim();
  if (!DOWNLOAD_MIME_TYPE_PATTERN.test(mainType)) {
    return "application/octet-stream";
  }
  return raw;
}

/**
 * Decodes base64 and saves via FileSaver (avoids ad-hoc anchor elements and href assignment).
 * @param {string} base64Content
 * @param {string} fileName
 * @param {string} [contentType]
 * @returns {boolean} whether the save completed without error
 */
export function downloadBase64AsFile(
  base64Content,
  fileName,
  contentType = "application/octet-stream"
) {
  try {
    if (!base64Content || !fileName) {
      return false;
    }
    const safeName = sanitizeDownloadFileName(fileName);
    const safeType = sanitizeDownloadContentType(contentType);
    const blob = b64toBlob(base64Content, safeType);
    FileSaver.saveAs(blob, safeName);
    return true;
  } catch {
    return false;
  }
}

export const openAndDownloadPdf = (res) => {
  const blob = b64toBlob(res, "application/pdf");
  if (isMobileTab()) {
    FileSaver.saveAs(blob);
  } else {
    const blobUrl = URL.createObjectURL(blob);
    const pdfWindow = window.open("");
    if (pdfWindow) {
      pdfWindow.document.write(
        "<iframe width='100%' height='100%' src='" + blobUrl + "'></iframe>"
      );
      pdfWindow.document.close();
    } else {
      const link = document.createElement("a");
      link.href = blobUrl;
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }
};

export function checkExpiredEvent_old(eventDetails) {
  const d = new Date();
  const currentOffset = d.getTimezoneOffset();
  const currentUTCTime = moment();
  const expireDate =
    eventDetails?.repeatedType !== 1 && eventDetails?.repeatedEndDate
      ? eventDetails?.repeatedEndDate
      : eventDetails?.endTime;
  const officeTime = moment(expireDate, "YYYY-MM-DDTHH:mm:ss")
    .add(eventDetails?.office?.state?.utcOffsetInMinutes, "m")
    .add(currentOffset, "m")
    .add(eventDetails?.office?.state?.utcOffsetInMinutes, "m")
    .add(currentOffset, "m")
    .format("YYYY-MM-DDTHH:mm:ss");

  return (
    currentUTCTime.diff(moment(officeTime, "YYYY-MM-DDTHH:mm:ss"), "days") > 1
  );
}

export function checkExpiredEvent(eventDetails) {
  const expireDate =
    eventDetails?.repeatedType !== 1 && eventDetails?.repeatedEndDate
      ? eventDetails?.repeatedEndDate
      : eventDetails?.endTime;

  let convertedExpDate = moment(
    moment.tz(expireDate, eventDetails?.timezoneId).format("YYYY-MM-DD"),
    "YYYY-MM-DD"
  );
  let convertedCurrentDate = moment
    .tz(new Date(), eventDetails?.timezoneId)
    .format("YYYY-MM-DD");

  return moment(convertedExpDate).isBefore(convertedCurrentDate);
}

export const scrollToError = () => {
  setTimeout(() => {
    const error = document.getElementsByClassName("error-msg");
    if (error && error.length) {
      error[0].scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "start",
      });
    }
  }, 600);
};

export const scrollToErrorInModal = (modalId) => {
  setTimeout(() => {
    const modalEl = document.getElementById(modalId);
    if (modalEl) {
      const errors = modalEl.getElementsByClassName("error-msg");
      if (errors && errors.length) {
        errors[0].scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "start",
        });
      }
    }
  }, 600);
};

export const converToMarker = (doctor) => {
  if (typeof doctor === "object" && "permanently_closed" in doctor) {
    delete doctor.permanently_closed;
  }

  const {
    geometry = {},
    opening_hours,
    id,
    name,
    place_id,
    vicinity,
    rating,
    photos,
    office,
    ...rest
  } = doctor;
  let profilePic = "";
  if (photos && photos.length) {
    profilePic = photos[0].getUrl();
  }
  const { lat, lng } = geometry.location || {};

  return {
    location: {
      lat: id ? office?.latitude || 0 : lat?.(),
      lng: id ? office?.longitude || 0 : lng?.(),
    },
    name,
    address: vicinity,
    placeId: place_id,
    overallRating: rating,
    profilePic,
    isGoogleDoctor: id ? false : true,
    id,
    office,
    ...rest,
  };
};
export const convertToMarkerList = (doctors) => {
  return doctors.map(converToMarker);
};

export const compose =
  (...fns) =>
  (x) =>
    fns.reduceRight((v, f) => f(v), x);

/**
This calculation based on the conversion of latitude degree to kms where 1 
degree of latitude change equals approximately 111.2 km. 
I am calculating bounds of the map from a latLng with 100 km width.
*/
export const getLatLngBounds = (lat, lng) => {
  const lat_change = 100 / 111;
  const lon_change = Math.abs(Math.cos(lat * (Math.PI / 180)));
  const sw_lat = lat - lat_change;
  const sw_lon = lng - lon_change;
  const ne_lat = lat + lat_change;
  const ne_lon = lng + lon_change;
  const sw = {
    lat: sw_lat,
    lng: sw_lon,
  };
  const ne = {
    lat: ne_lat,
    lng: ne_lon,
  };
  return {
    sw,
    ne,
  };
};
export const validateMobileNumber = (number, min = 9, max = 16) => {
  const pattern = `^\\+?[\\d\\s\\-\\(\\)\\.]{${min},${max}}$`;
  return new RegExp(pattern).test(number);
};
export const validateNumber = (number, min = 9, max = 16) => {
  //eslint-disable-next-line
  return (
    /^\+?[\(\.)\d-]+$/.test(number) &&
    number.length >= min &&
    number.length <= max
  );
};

export const validateEmail = (email) => {
  //eslint-disable-next-line
  return /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
    email
  );
};

/**
 * Parser that strips whitespaces away from a phone number
 * string so that the plain number can be stored.
 */
export const parseNumber = (value) =>
  (value ? value.replace(/\s/g, "") : "").replace(/[^\d+]/g, "");

export const getTotalPossiblePages = (pageSize, totalItems) => {
  const itemsInLastPage = totalItems % pageSize;
  return [Math.ceil(totalItems / pageSize), itemsInLastPage];
};

export const paginateArray = (array, page_size, page_number) => {
  // human-readable page numbers usually start with 1, so we reduce 1 in the first argument
  return array.slice((page_number - 1) * page_size, page_number * page_size);
};

export const handleError = (err, options = {}) =>
  toast.error(err?.message, options);
export const handleSuccess = (message, options = {}) =>
  toast.success(message, options);

export const sortAlphabetically = (data, sortKey) => {
  if (!data || !sortKey) return data;
  if (!Array.isArray(data)) return data;
  return sortBy(data, (item) =>
    item[sortKey]?.replaceAll(" ", "").toLowerCase()
  );
};

export const addDefaultStaffRounded = (e, he) => {
  if (e?.target) {
    e.target.src = defaultStaffRounded;
    e.target.onerror = null;
  }
};

export const addDefaultStaffCircular = (e) => {
  if (e?.target) {
    e.target.src = defaultStaffCircular;
    e.target.onerror = null;
  }
};

export const addDefaultSrc = (e, src) => {
  if (e?.target) {
    e.target.src = src;
    e.target.onerror = null;
  }
};

export function getWeekDay(date) {
  const weekday = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];
  let day = weekday[moment(date).day()];
  return day?.substring(0, 3);
}

export function titleWordLimit(text) {
  if (text?.length <= 80) {
    return text;
  } else {
    return `${text?.slice(0, 80)}...`;
  }
}

export function testRegexCheck(text) {
  let regex = new RegExp(/^[\w\-.,'\s]*$/);
  return regex.test(text);
}

export function testNumberRegexCheck(text) {
  let regex = new RegExp(/^\d*$/);
  return regex.test(text);
}

export function testNumberRegexCheckForZeroToOne(text) {
  let regex = new RegExp(/^$|^0$|^0\.$|^0\.[1-9]$|^1$|^1\.$|^1\.0$/);
  return regex.test(text);
}

export function testRegexCheckDescription(text) {
  let regex = new RegExp(/^[\w-_.!@#$%^&+*()?/, '".\n]*$/);
  return regex.test(text);
}

export const sliceText = (text, maxLen = 10) => {
  if (!text) return text;
  if (typeof text === "string" || text instanceof String) {
    return text.length > maxLen ? text.slice(0, maxLen).concat(" ...") : text;
  }
  return text;
};

export const addToCalenderText = (eventOrShift) => {
  return (
    <>
      <p>
        The download and add to calendar allows you to add the selected{" "}
        {eventOrShift} to your personal calendar on the web.
      </p>
      <p>
        To add the selected {eventOrShift} on your personal calendar, follow the
        below-mentioned steps:
      </p>
      <ol>
        <li>Click on the “Download and Add to Calendar” link.</li>
        <li>
          Once selected, the {eventOrShift} file will be downloaded to your
          device. Open “Downloads” or the folder where the downloaded files are
          stored on your desktop/laptop.
        </li>
        <li>
          Right click on the {eventOrShift} downloaded file and select “Open
          with”.
        </li>
        <li>Select the calendar where you want to add the {eventOrShift}.</li>
        <li>
          Once the calendar is selected, it will show you the details of the
          {eventOrShift} such as the title of the {eventOrShift}, date and time
          of the {eventOrShift}, duration of the {eventOrShift} etc. If you
          want, you can edit these details. Click on “Save” to add the{" "}
          {eventOrShift} in your personal calendar.
        </li>
      </ol>
      <p>
        Once “Save’ is selected, the {eventOrShift} will be added to your
        personal calendar. If you want, you can remove the {eventOrShift} from
        your calendar by deleting it from your calendar.
      </p>
    </>
  );
};

export const cacheSideBarActive = (key) => {
  if (localStorage.getItem(key ? key : "isSidebarActive")) {
    return localStorage.getItem(key ? key : "isSidebarActive") == "true"
      ? true
      : false;
  }
  return isMobileTab() ? false : true;
};

export const setCacheLocalstorage = (key, value, needStringify = false) => {
  return localStorage.setItem(
    key,
    needStringify ? JSON.stringify(value) : value
  );
};
export const getCacheLocalstorage = (key, needParsing = false) => {
  const value = localStorage.getItem(key);
  if (needParsing) {
    return JSON.parse(value);
  }
  return value;
};

export const inBytes = (inMbs) => inMbs * 1024 * 1024;

export const getBlobnameFromUrl = (blobUrl, containerName) => {
  const blobName = blobUrl?.split(`${containerName}/`)?.[1];
  return blobName;
};

export const toNormalizeString = (str) => {
  if (!str) return str;
  return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
};

export const setStorage = (key, value, alreadyStringyFied = false) => {
  sessionStorage.setItem(
    key,
    alreadyStringyFied ? value : JSON.stringify(value)
  );
};

export const getStorage = (key) => {
  try {
    return JSON.parse(sessionStorage.getItem(key));
  } catch {
    return null;
  }
};
export const sortedOwnerList = (ownerList) => {
  return sortBy(ownerList, [(owner) => owner.firstName?.toLowerCase()]);
};

export const removeStorage = (arrayOfkey = []) => {
  arrayOfkey.forEach((item) => {
    sessionStorage.removeItem(item);
  });
};

export const handleKeyDownForNumberInput = (e) => {
  if (["e", "E", "+", "-"].includes(e?.key)) {
    e.preventDefault();
  }
  if (["."].includes(e?.key) && e?.target?.value?.length < 1) {
    e.preventDefault();
  }
};
export const isValueEmpty = (value) => {
  if (!value) return "--";
  return value;
};

export const convertIntoTwoDecimal = (price) => {
  if (!price && typeof price != "number") {
    return "--";
  }

  return parseFloat(price).toFixed(2);
};

export const convertToYYYYMMDDFormat = (dateString) => {
  if (!dateString) return "--";
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const monthString = month < 10 ? "0" + month : month;
  const dayString = day < 10 ? "0" + day : day;
  return `${year}-${monthString}-${dayString}`;
};

export const isOver18Years = (birthDate) => {
  if (!birthDate) return false;
  const birthDateString = convertToYYYYMMDDFormat(birthDate);
  const today = new Date();
  const birthDateObj = new Date(birthDateString);
  const age = today.getFullYear() - birthDateObj.getFullYear();
  const monthDiff = today.getMonth() - birthDateObj.getMonth();
  if (
    monthDiff < 0 ||
    (monthDiff === 0 && today.getDate() < birthDateObj.getDate())
  ) {
    return age - 1 >= 18;
  }
  return age >= 18;
};

export const convertTimeMinuteToHour = (timeInMin) => {
  if (!timeInMin) return;
  const duration = moment.duration(timeInMin, "minutes");
  const durationInHours = duration.asHours().toFixed(2);

  return durationInHours;
};

export const convertTimeMinuteToDays = (timeInMin) => {
  if (!timeInMin) return;
  const duration = moment.duration(timeInMin, "minutes");
  const durationInHours = duration.asDays();

  return durationInHours;
};

export const convertDateFormat = (
  dateTimeToConvert,
  formatOfDate,
  inLocalTime
) => {
  if (!dateTimeToConvert) return;
  const localTime = moment.utc(dateTimeToConvert).local();
  const formattedDate = moment(
    inLocalTime ? localTime : dateTimeToConvert
  ).format(formatOfDate);
  return formattedDate;
};

export function calculateCompleteionTime(timeInMinutes) {
  if (timeInMinutes == null) {
    return "";
  } else {
    let Hours = Math.floor(timeInMinutes / 60);
    let minutes = timeInMinutes % 60;
    let compute = parseFloat(Hours + "." + Math.round((minutes / 60) * 100));
    let computeTime = compute.toString() + " Hours ";
    return computeTime;
  }
}

export const formatUTCDateTime = (textDate) => {
  if (typeof textDate == "undefined" || textDate == null) return "";
  else return moment.utc(textDate).local().format("MMM D, YYYY");
};

/**
 * Formats a calendar date without shifting the day for API ISO strings.
 * Uses the UTC calendar date (e.g. 2025-12-29T07:02:19.48Z → Dec 29). Local Date values
 * from date pickers are formatted in local time.
 */
export const formatCalendarDate = (dateValue, format = "MMM D, YYYY") => {
  if (dateValue == null || dateValue === "") return "";
  if (typeof dateValue === "string") {
    return moment.utc(dateValue).format(format);
  }
  return moment(dateValue).format(format);
};

/**
 * Parses an API ISO date string into a local Date at midnight for the UTC calendar day.
 * Use when populating date pickers so values like 2026-06-20T00:00:00Z stay June 20 in any timezone.
 */
export const parseCalendarDateToLocalDate = (dateValue) => {
  if (dateValue == null || dateValue === "") return null;
  if (dateValue instanceof Date) {
    const d = new Date(dateValue);
    d.setHours(0, 0, 0, 0);
    return d;
  }
  if (typeof dateValue === "string") {
    const m = moment.utc(dateValue);
    if (!m.isValid()) return null;
    const d = new Date(m.year(), m.month(), m.date());
    d.setHours(0, 0, 0, 0);
    return d;
  }
  return null;
};

/**
 * Formats a contract signature "Date" field for API payloads without UTC day-shift.
 * Uses the local calendar day (what the date picker shows), as DD-MM-YYYY.
 * @param {string|Date|null|undefined} dateValue
 * @returns {string|null|undefined}
 */
export const formatContractDetailDateForApi = (dateValue) => {
  if (dateValue == null || dateValue === "") return dateValue;
  if (dateValue instanceof Date) {
    return moment(dateValue).format("DD-MM-YYYY");
  }
  if (typeof dateValue === "string") {
    if (/^\d{2}-\d{2}-\d{4}$/.test(dateValue)) {
      return dateValue;
    }
    // ISO / YYYY-MM-DD: use local calendar day to match the picker display
    const local = moment(dateValue);
    if (local.isValid()) {
      return local.format("DD-MM-YYYY");
    }
  }
  return dateValue;
};

/**
 * Ensures ContractDetails "Date" items send date-only strings (no Date→toISOString UTC shift).
 * @param {Array<object>|null|undefined} contractDetails
 * @returns {Array<object>|null|undefined}
 */
export const normalizeContractDetailsDatesForApi = (contractDetails) => {
  if (!Array.isArray(contractDetails)) return contractDetails;
  return contractDetails.map((item) => {
    if (item?.title !== "Date") return item;
    return {
      ...item,
      text: formatContractDetailDateForApi(item.text),
    };
  });
};

/** Formats an API timestamp in the user's local timezone (date and time). */
export const formatLocalDateTime = (dateValue, format = "MMM D, YYYY h:mm A") => {
  if (dateValue == null || dateValue === "") return "";
  return moment.utc(dateValue).local().format(format);
};

export function createDateStringForAPIRequest(date) {
  const datetime = moment(date).utc().toISOString();
  return datetime;
}

/**
 * Formats a date for API requests using local date/time components in ISO-like form
 * (e.g. 2026-05-20T05:41:13.807Z). The trailing Z is literal; values are not converted to UTC.
 */
export function createLocalDateStringForAPIRequest(date) {
  if (date == null || date === "") return null;
  return moment(date).format("YYYY-MM-DDTHH:mm:ss.SSS[Z]");
}

export function createDateStringForAPIRequestDueDate(date) {
  const dueDate = moment(date).set({
    hour: 23,
    minute: 59,
    second: 0,
    millisecond: 0,
  });
  const datetime = dueDate.utc().toISOString();
  return datetime;
}

/**
 * Formats a due date for API requests using local end-of-day components with a literal Z
 * (e.g. 2026-07-12T23:59:00.000Z). Does not convert to UTC, so the calendar day is preserved.
 */
export function createLocalDateStringForAPIRequestDueDate(date) {
  if (date == null || date === "") return null;
  return moment(date)
    .set({ hour: 23, minute: 59, second: 0, millisecond: 0 })
    .format("YYYY-MM-DDTHH:mm:ss.SSS[Z]");
}

export function createDateStringForAPIRequestEdit(date, oldDate) {
  const prevDate = new Date(oldDate);
  date.setHours(
    prevDate.getHours(),
    prevDate.getMinutes(),
    prevDate.getSeconds(),
    0
  );
  let datetime = new Date(date.toUTCString()).toISOString();
  return datetime;
}

export const convertDateReceivedFromApiEdit = (dateString) => {
  let date = new Date(new Date(dateString).toUTCString());
  return date;
};

export const downloadPDF = (base64String, fileName) => {
  // Decode the Base64 string into binary data
  const binaryData = atob(base64String);

  // Convert binary data to an array of 8-bit unsigned integers
  const byteArray = new Uint8Array(binaryData.length);
  for (let i = 0; i < binaryData.length; i++) {
    byteArray[i] = binaryData.charCodeAt(i);
  }

  // Create a Blob object from the byte array
  const blob = new Blob([byteArray], { type: "application/pdf" });

  // Create a URL for the Blob object
  const url = URL.createObjectURL(blob);

  // Create an anchor element and trigger a download
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();

  // Clean up by revoking the Blob URL and removing the anchor element
  URL.revokeObjectURL(url);
  document.body.removeChild(link);
};

export const canAccessAiAssistantChat = (userProfileData) => {
  let isAccessible;

  if (
    userProfileData?.role?.systemRole == constants?.systemRoles?.accountOwner
  ) {
    return (isAccessible = userProfileData?.isAiSettingsEnabled);
  }

  if (userProfileData?.role?.systemRole == constants?.systemRoles?.staff) {
    return (isAccessible = JSON.parse(
      localStorage.getItem("selectedOwner")
    )?.isAiSettingsEnabled);
  }
};

export function generateDeviceId() {
  const STORAGE_KEY = "device_id";

  let deviceId = localStorage.getItem(STORAGE_KEY);

  if (!deviceId) {
    deviceId = uuidv4();
    localStorage.setItem(STORAGE_KEY, deviceId);
  }

  return deviceId;
}

const detectBrowser = (userAgent) => {
  if (userAgent.includes("Chrome")) return "chrome";
  if (userAgent.includes("Safari")) return "Safari";
  if (userAgent.includes("Firefox")) return "firefox";
  if (userAgent.includes("Edge")) return "edge";
  if (userAgent.includes("Opera") || userAgent.includes("OPR")) return "opera";
  return "unknown";
};

const detectPlatform = (userAgent) => {
  if (userAgent.includes("Win")) return "windows";
  if (userAgent.includes("Mac")) return "macOS";
  if (userAgent.includes("Linux")) return "Linux";
  if (userAgent.includes("Android")) return "android";
  if (userAgent.includes("iPhone") || userAgent.includes("iPad")) return "iOS";
  return "unknown";
};

export const getDeviceType = () => {
  const userAgent = navigator?.userAgent;
  const deviceType =
    /Mobi|Android|iPhone|iPad|iPod|BlackBerry|Opera Mini/i.test(userAgent)
      ? "mobile"
      : "desktop";
  const platform = detectPlatform(userAgent);
  const browser = detectBrowser(userAgent);
  return `${deviceType}-${platform}-${browser}`;
};

export const extractFilename = (url) => {
  if (!url) return null;
  // Split the URL by '/' and get the last part
  return url.split("/").pop();
};

async function generateMapImageWithMarker(
  { address, latitude, longitude, sasToken },
  markerColor = "red"
) {
  const apiKey = process.env.REACT_APP_GOOGLE_API_KEY; // Replace with your actual API key
  const baseUrl = "https://maps.googleapis.com/maps/api/staticmap";
  let center;

  if (latitude && longitude) {
    center = `${latitude},${longitude}`;
  } else if (address) {
    center = encodeURIComponent(address);
  } else {
    console.error(
      "Error: Please provide either an address or latitude and longitude."
    );
    return null;
  }

  const params = {
    center: center,
    zoom: 14,
    size: "600x300",
    markers: `color:${markerColor}|${center}`,
    key: apiKey,
  };

  const queryString = new URLSearchParams(params).toString();
  const url = `${baseUrl}?${queryString}`;

  // Fetch the image data
  const response = await fetch(url);
  const imageBlob = await response.blob();

  // Upload the image to Azure Blob Storage
  const azureBlobUrl = await uploadImageToAzureBlobStorage(imageBlob, sasToken);

  return azureBlobUrl; // Return the URL of the uploaded image
}

export const calculateReminderHours = (reminderBefore, reminderBeforeType) => {
  let hours = 0;
  switch (reminderBeforeType) {
    case 1:
      hours = reminderBefore;
      break;
    case 2:
      hours = reminderBefore * 24;
      break;
    case 3:
      hours = reminderBefore * 30 * 24;
      break;
    case 4:
      hours = reminderBefore * 12 * 30 * 24;
      break;
    default:
      hours = 0;
  }
  return hours;
};

async function uploadImageToAzureBlobStorage(imageBlob, sasToken) {
  // Your logic to upload the image to Azure Blob Storage
  const accountName = process.env.REACT_APP_AZURE_STORAGE_ACCOUNT;
  const sasString = sasToken;
  const containerName = process.env.REACT_APP_AZURE_STORAGE_CONTAINER;

  const pipeline = newPipeline(new AnonymousCredential());
  const blobServiceClient = new BlobServiceClient(
    `https://${accountName}.blob.core.windows.net?${sasString}`,
    pipeline
  );

  const containerClient = blobServiceClient.getContainerClient(containerName);
  const blobName = `${uuidv4()}.png`; // Assuming the image format is PNG

  const blockBlobClient = containerClient.getBlockBlobClient(blobName);
  const blobOptions = {
    blobHTTPHeaders: { blobContentType: "image/png" }, // Assuming the image format is PNG
  };

  // Upload the image Blob to Azure Blob Storage
  await blockBlobClient.uploadData(imageBlob, blobOptions);

  // Return the URL of the uploaded image
  const azureBlobUrl = `https://${accountName}.blob.core.windows.net/${containerName}/${blobName}`;
  return azureBlobUrl;
}

export { generateMapImageWithMarker };

export function haveSameNumericValues(arr1, arr2) {
  // Sort both arrays and check if every item matches
  const sortedArr1 = [...arr1].sort(); // Create a copy of arr1 and sort it
  const sortedArr2 = [...arr2].sort(); // Create a copy of arr2 and sort it

  for (let i = 0; i < sortedArr1.length; i++) {
    if (sortedArr1[i] !== sortedArr2[i]) {
      return false; // Found a mismatch
    }
  }

  return true; // All values match
}
export const downloadFile = (url) => {
  const link = document.createElement("a");
  link.href = url;
  const splitFile = url.split("/");
  const fileName = splitFile[splitFile.length - 1];
  link.download = fileName || "staffcollection.xlsx"; // optional: rename file on download
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
